<template>
    <div class="apprentice-simple-card horizontal-timeline-wrapper">
        <div class="component-title">
            <h5 class="title">
                Tijdlijn

                <div class="actions">
                    <b-button
                        class="btn-add btn-add-time-lime"
                        v-if="!teacherAccess"
                        v-b-tooltip.hover
                        variant="primary-icon"
                        :title="$t('profile.add_timeline_item')"
                        @click="showNewProfileCardModal">
                    </b-button>
                </div>
            </h5>
        </div>

        <div
            class="timeline-wrapper"
            v-if="items.length > 0">
            <div class="controls">
                <b-button
                    class="btn-chevron-left btn-prev"
                    variant="primary"
                    @click="handleClick('previous')">
                </b-button>

                <b-button
                    class="btn-chevron-right btn-next"
                    variant="primary"
                    @click="handleClick('next')">
                </b-button>
            </div>

            <div class="timeline-items">
                <design-2021-horizontal-timeline-card-component
                    v-for="item of items"
                    :user-credentials-in="userCredentialsIn"
                    :teacher-access="teacherAccess"
                    :key="item.id"
                    :timeline-item-in="item">
                </design-2021-horizontal-timeline-card-component>
            </div>

            <b-button
                class="btn-show-all"
                @click="openTimeline">
                toon alles
            </b-button>
        </div>

        <div
            class="not-found with-header no-timeline-found"
            v-else>
            <div
                class="btn-add-circle icon"
                v-b-tooltip.hover
                v-if="!teacherAccess"
                :title="$t('profile.add_timeline_item')"
                @click="showNewProfileCardModal"></div>

            <div
                class="icon"
                v-else>
                <i class="fas fa-exclamation-circle"></i>
            </div>

            <div class="alert-notice assignments">
                {{ $t('profile.no_timeline_items_found') }}
            </div>
        </div>

        <design-2021-profile-add-card-component
            ref="newProfileCard"
            :categories-in="categoriesIn"
            :seasons-in="seasonsIn"
            @add-card="addCard">
        </design-2021-profile-add-card-component>
    </div>
</template>

<script>
export default {
    name: 'design-2021-horizontal-timeline-component',
    props: {
        timelineItemsIn: {
            type: Array,
            required: true,
        },

        teacherAccess: {
            type: Boolean,
            required: true,
        },

        userCredentialsIn: {
            type: Object,
            required: true,
        },

        categoriesIn: {
            type: Array,
            required: true,
        },

        seasonsIn: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            timelineItems: this.timelineItemsIn,
            profileUrl: this.teacherAccess
                ? this.routes.profile() + '/' + this.userCredentialsIn.id
                : this.routes.profile(),
        };
    },

    mounted() {
        const wrapper = document.querySelector('.timeline-items');

        if (wrapper) {
            wrapper.addEventListener('scrollend', (event) => {
                const slides = document.getElementsByClassName('timeline-card');
                const btnNext = document.querySelector('.controls .btn-next');
                const btnPrevious = document.querySelector('.controls .btn-prev');

                if (this.isElementVisibleWithinContainer(slides[0])) {
                    btnNext.classList.remove('disabled');
                    btnPrevious.classList.add('disabled');
                } else if (this.isElementVisibleWithinContainer(slides[slides.length - 1])) {
                    btnNext.classList.add('disabled');
                    btnPrevious.classList.remove('disabled');
                }
            });
        }
    },

    methods: {
        openTimeline() {
            window.location.href = this.profileUrl;
        },

        handleClick(direction) {
            const wrapper = document.querySelector('.timeline-items');
            const slide = document.querySelector('.timeline-card');
            const slideWidth = slide.offsetWidth + 15;

            if (direction === 'previous') {
                wrapper.scrollBy({ left: -slideWidth, behavior: 'smooth' });
            } else {
                wrapper.scrollBy({ left: slideWidth, behavior: 'smooth' });
            }
        },

        isElementVisibleWithinContainer(element) {
            const containerBox = document.querySelector('.timeline-items').getBoundingClientRect();
            const ElBox = element.getBoundingClientRect();

            return (
                ElBox.top >= containerBox.top &&
                ElBox.left >= containerBox.left &&
                ElBox.bottom <= containerBox.bottom &&
                ElBox.right <= containerBox.right
            );
        },

        addCard(data) {
            this.alert.saving();
            this.axios.post(this.routes.profileCard(), data).then(
                (response) => {
                    this.timelineItems.push(response.data);
                    this.timelineItems = this.timelineItems.sort((a, b) => {
                        return new Date(b.date) - new Date(a.date);
                    });

                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        showNewProfileCardModal() {
            this.$refs.newProfileCard.show(null);
        },
    },

    computed: {
        items() {
            if (this.timelineItems.length === 0) {
                return [];
            }

            return this.timelineItems.slice(0, 7).map((card) => {
                if (!this.categoriesIn.some((category) => category.id === card.category_id)) {
                    card.category_id = 0;
                    return {
                        ...card,
                        category: {
                            color: '#07A1FF',
                            id: 0,
                            title: this.$t('profile.not_linked'),
                        },
                    };
                } else {
                    return card;
                }
            });
        },
    },
};
</script>

<style scoped lang="scss">
.component-title {
    .title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        column-gap: $grid-gutter-width;
        width: 100%;
    }

    .actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');

        .btn {
            &:before {
                margin: 0;
            }
        }
    }
}

.timeline-wrapper {
    position: relative;
    top: -40px;
}

.controls {
    position: relative;
    display: flex;
    top: 69px;
    align-items: center;
    justify-content: space-between;
    column-gap: calc(100% - 66px);

    .btn {
        border-radius: 50%;
        width: 33px;
        height: 33px;
        z-index: 50;

        &:before {
            margin: 0;
            width: auto;
        }
    }

    &:after {
        position: absolute;
        content: '';
        display: block;
        height: 3px;
        width: 100%;
        background-color: $border-color;
        border-radius: $border-radius-content;
    }
}

.timeline-items {
    display: flex;
    align-items: stretch;
    justify-content: flex-start;
    column-gap: map-get($grid-gutter-spacing, 'fifteen');
    flex-wrap: nowrap;
    overflow-x: scroll;
    scroll-snap-type: x mandatory;
    scroll-padding: 50%;
    scrollbar-width: none;
    width: 90%;
    margin: 0 auto;

    ::v-deep .timeline-card {
        flex: 0 0 100%;
    }
}

.btn-show-all {
    margin-left: auto;
    margin-top: map-get($grid-gutter-spacing, 'forty-two');

    &:after {
        padding-top: 1px;
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .timeline-items {
        ::v-deep .timeline-card {
            flex: 0 0 calc(50% - 15px);
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xxl')) {
    .timeline-items {
        ::v-deep .timeline-card {
            flex: 0 0 calc(25% - 15px);
        }
    }
}
</style>
